import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatISO, subMonths } from 'date-fns'
import { downloadFile } from '@/utils/download'
import { Alert, Box, Button, Paper, Typography } from '@mui/material'
import { ComingSoonChip } from '@/components'
import { createFileRoute, createLink, redirect, useRouter } from '@tanstack/react-router'
import {
  type Document,
  type DocumentAction,
  DocumentList,
  type PaginationModel
} from '@/components/DocumentList/DocumentList'
import { PageHeader } from '@/components/PageHeader/PageHeader.tsx'
import { useReportList } from '@/features/reports/hooks/useReportList'
import { SiteChips } from '@/components/SiteChips/SiteChips.tsx'
import { usePermissions } from '@/hooks/usePermissions'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import DownloadIcon from '@mui/icons-material/Download'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import CheckIcon from '@mui/icons-material/Check'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { z } from 'zod'
import { FEATURES, isFeatureEnabled } from '@/utils/feature-flag'
import { CoercedStringArray } from '@/utils/common.schema'
import { useCompany } from '@/contexts/CompaniesContext'
import { ConfirmDialog } from '@/components/Dialog/ConfirmDialog.tsx'
import { useReportDelete } from '@/features/reports/hooks/useReportDelete.tsx'
import { ReportingOverview } from '@/features/mocked/reporting'
import { useMessageSnackbar } from '@/hooks/useMessageSnackbar.ts'
import { useErrorMessageTranslation } from '@/hooks/useErrorMessageTranslation.ts'
import { AppTabs } from '@/components/Tab/AppTab.tsx'
import { useSiteFilterShape } from '@/hooks/useSiteFilterShape.ts'
import { useFormBuilderSearch } from '@/components/FormBuilder/formBuilderSearch.ts'
import { PageSearchFilter } from '@/components/PageSearchFilter/PageSearchFilter.tsx'

const SearchSchema = z.object({
  success: z.boolean().optional(),
  sites: CoercedStringArray.optional(),
  minDate: z.string().optional(),
  maxDate: z.string().optional()
})

export const Route = createFileRoute('/_authenticated/$companySlug/reports/')({
  beforeLoad: ({ context }) => {
    // Check if reports feature is enabled
    if (!isFeatureEnabled(FEATURES.REPORTS)) {
      throw redirect({
        to: '/not-found'
      })
    }

    context.auth.rejectIfMissingPermissions(['REPORTING_READ'])
  },
  component: ReportsPage,
  validateSearch: SearchSchema
})

const showReportingTab = isFeatureEnabled(FEATURES.REPORTING)

function ReportsPage() {
  const { t } = useTranslation('_routes')

  const tabs = useMemo(
    () => [
      {
        label: t('routes.reports.main.tabs.overview'),
        value: 'overview' as const,
        content: () => <ReportingOverview />
      },
      {
        label: t('routes.reports.main.tabs.weeklyReports'),
        value: 'weekly-reports' as const,
        content: () => <WeeklyReports showHeader={false} />
      }
    ],
    [t]
  )

  if (!showReportingTab) {
    return <WeeklyReports />
  }

  return (
    <>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PageHeader>{t('routes.reports.main.list.title')}</PageHeader>
          <ComingSoonChip label={t('routes.reports.main.list.comingSoon')} />
        </Box>
      </Box>

      <AppTabs tabs={tabs} />
    </>
  )
}

const LinkButton = createLink(Button)

function WeeklyReports({ showHeader = true }: { showHeader?: boolean }) {
  const success = Route.useSearch({ select: s => s.success })
  const { sites } = useCompany()

  const navigate = Route.useNavigate()
  const { hasPermissions } = usePermissions()
  const { showSuccess, showError } = useMessageSnackbar()
  const { translateError } = useErrorMessageTranslation()

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [toast, setToast] = useState<string>()
  const [documentToBeDeleted, setDocumentToBeDeleted] = useState<Document | null>(null)
  const [paginationModel, setPaginationModel] = useState<PaginationModel>({
    page: 0,
    pageSize: 10
  })
  const { t } = useTranslation('_routes')
  const router = useRouter()

  useEffect(() => {
    if (success) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setToast('routes.reports.main.messages.uploadSuccess')
      // Clear the param to prevent toast on refresh
      navigate({
        search: prev => ({ ...prev, success: undefined }),
        replace: true
      })
    }
  }, [navigate, success])

  const siteFilterShape = useSiteFilterShape()

  const filterSchema = useMemo(() => {
    return z.object({
      ...siteFilterShape,
      timeRange: z
        .tuple([
          z.coerce.date<Date>().meta({ label: 'start', withTime: false }).optional(),
          z.coerce.date<Date>().meta({ label: 'end', withTime: false }).optional()
        ])
        .optional()
        .describe(t('routes.reports.main.list.filters.timeRange'))
    })
  }, [siteFilterShape, t])

  const { values: filterValue, persistOnSubmit } = useFormBuilderSearch(filterSchema)

  const filterValueOverride = useMemo(() => {
    return {
      ...filterValue,
      timeRange: [
        filterValue.timeRange?.[0] ?? subMonths(new Date(), 2),
        filterValue.timeRange?.[1] ?? new Date()
      ] as [Date, Date]
    }
  }, [filterValue])

  // // Reset page when filters change
  useEffect(() => {
    setPaginationModel(prev => ({ ...prev, page: 0 }))
  }, [searchQuery, filterValueOverride])

  // Build filters for the API
  const filters = useMemo(() => {
    return {
      siteIds: filterValueOverride.sites,
      search: searchQuery || undefined,
      minDate: filterValueOverride.timeRange[0],
      maxDate: filterValueOverride.timeRange[1]
    }
  }, [searchQuery, filterValueOverride])

  // Fetch reports from API
  const { data, isFetching } = useReportList(
    {
      search: filters.search,
      siteIds: filters.siteIds?.map(enumValue => enumValue.toString()),
      minDate: formatISO(filters.minDate, { representation: 'date' }),
      maxDate: formatISO(filters.maxDate, { representation: 'date' })
    },
    undefined,
    paginationModel
  )

  // Transform API data to Document format
  const documents: Document[] = useMemo(() => {
    if (!data?.content) return []

    return data.content.map(report => ({
      id: report.id.toString(),
      name: report.name,
      icon: DescriptionOutlinedIcon,
      date: new Date(report.date),
      details: <SiteChips siteIds={report.siteIds} sites={sites} />
    }))
  }, [data, sites])

  const deleteMutation = useReportDelete()

  const handleEdit = (document: Document) => {
    router.navigate({
      to: `/$companySlug/reports/edit/${document.id}`
    })
  }

  const handleDelete = (document: Document) => {
    setDocumentToBeDeleted(document)
  }

  const confirmDelete = () => {
    if (documentToBeDeleted) {
      deleteMutation.mutate(
        {
          reportId: documentToBeDeleted.id
        },
        {
          onSuccess: () => {
            showSuccess(t('routes.reports.main.list.actions.delete.success'))
          },
          onError: error =>
            showError(
              t('routes.reports.main.list.actions.delete.error', {
                message: translateError(error)
              })
            )
        }
      )
      setDocumentToBeDeleted(null)
    }
  }

  const handleBulkDownload = (selectedIds: string[]) => {
    // TODO: Implement bulk download
    console.log(`Downloading ${selectedIds.length} report(s)`)
  }

  const handleBulkDelete = (selectedIds: string[]) => {
    // TODO: Implement bulk delete
    console.log(`Deleting ${selectedIds.length} report(s)`)
  }

  // Define actions for each document row
  const actions: DocumentAction[] = [
    {
      label: t('routes.reports.main.list.actions.download'),
      icon: <FileDownloadIcon fontSize="small" />,
      onClick: (document, progressHandler) =>
        downloadFile(
          `/api/v1/reports/${document.id}/download`,
          document.name,
          progressHandler
        )
    }
  ]
  if (hasPermissions(['REPORTING_WRITE'])) {
    actions.push({
      label: t('routes.reports.main.list.actions.edit'),
      icon: <EditOutlinedIcon fontSize="small" />,
      onClick: handleEdit
    })
  }
  if (hasPermissions(['REPORTING_DELETE'])) {
    actions.push({
      label: t('routes.reports.main.list.actions.delete.button'),
      icon: <DeleteOutlineIcon fontSize="small" />,
      onClick: handleDelete
    })
  }

  return (
    <>
      {showHeader && (
        <Box
          sx={{
            mb: 4,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <PageHeader subtitle={t('routes.reports.main.list.subtitle')}>
            {t('routes.reports.main.list.title')}
          </PageHeader>
          {hasPermissions(['REPORTING_WRITE']) && (
            <LinkButton
              from={Route.fullPath}
              to="/$companySlug/reports/import"
              variant="contained"
              startIcon={<UploadFileIcon />}
            >
              {t('routes.reports.main.list.importButton')}
            </LinkButton>
          )}
        </Box>
      )}

      {!showHeader && hasPermissions(['REPORTING_WRITE']) && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <LinkButton
            from={Route.fullPath}
            to="/$companySlug/reports/import"
            variant="contained"
            startIcon={<UploadFileIcon />}
          >
            {t('routes.reports.main.list.importButton')}
          </LinkButton>
        </Box>
      )}

      <Paper sx={{ p: 3 }}>
        {toast && (
          <Alert
            icon={<CheckIcon fontSize="inherit" />}
            severity="success"
            sx={{ mb: 3 }}
          >
            {t(toast, { defaultValue: toast })}
          </Alert>
        )}

        {/* Filter Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1.5,
            mb: 3
          }}
        >
          <PageSearchFilter
            textFilter={{
              value: searchQuery,
              onChange: setSearchQuery,
              placeholder: t('routes.reports.main.list.searchPlaceholder')
            }}
            buttonFilter={{
              schema: filterSchema,
              onSubmit: persistOnSubmit(),
              defaultValues: filterValueOverride
            }}
          />

          {/* Bulk Actions */}
          {selectedIds.length > 0 && (
            <>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => handleBulkDownload(selectedIds)}
                size="small"
              >
                {t('routes.reports.main.list.bulkActions.download', {
                  count: selectedIds.length
                })}
              </Button>
              {hasPermissions(['REPORTING_DELETE']) && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => handleBulkDelete(selectedIds)}
                  size="small"
                >
                  {t('routes.reports.main.list.bulkActions.delete', {
                    count: selectedIds.length
                  })}
                </Button>
              )}
            </>
          )}
        </Box>

        {/* Date Range Summary */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
          {t('routes.reports.main.list.dateRange.title', {
            minDate: filters.minDate,
            maxDate: filters.maxDate,
            formatParams: {
              minDate: { month: 'short', day: 'numeric', year: 'numeric' },
              maxDate: { month: 'short', day: 'numeric', year: 'numeric' }
            }
          })}
        </Typography>

        {/* Document List */}
        <DocumentList
          documents={documents}
          actions={actions}
          selectable={true}
          onSelectionChange={setSelectedIds}
          isLoading={isFetching}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          totalItems={data?.totalItems ?? 0}
          pageSizeOptions={[5, 10, 25, 50, 100]}
        />
      </Paper>
      <ConfirmDialog
        title={t('routes.reports_delete.confirmDialog.title')}
        variant={'warning'}
        onClose={() => setDocumentToBeDeleted(null)}
        open={!!documentToBeDeleted}
        actions={[
          {
            label: t('routes.reports_delete.confirmDialog.buttons.cancel'),
            variant: 'outlined',
            onClick: () => {
              setDocumentToBeDeleted(null)
            }
          },
          {
            label: t('routes.reports_delete.confirmDialog.buttons.delete'),
            variant: 'contained',
            onClick: () => {
              confirmDelete()
            }
          }
        ]}
      >
        {t('routes.reports_delete.confirmDialog.subtitle')}
      </ConfirmDialog>
    </>
  )
}
